'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import { DEFAULT_FRAME_H, DEFAULT_FRAME_W, DEFAULT_SCALE, roamWalkRow } from './constants'
import { PetCanvasSprite } from './PetCanvasSprite'
import type { PetInfo, PetState, Point } from './types'
import { usePetRoam } from './usePetRoam'

export interface HermesSpriteProps {
  /** Source URL to spritesheet. Defaults to /sprites/hermes-sprites/hermes-sprite.png */
  src?: string
  /** Pet info override */
  info?: PetInfo
  /** Enable wandering roaming mode. Default: true */
  roam?: boolean
  /** Scale scalar multiplier. Default: 0.33 */
  scale?: number
  /** State override (idle, wave, jump, run, failed, review, waiting) */
  state?: PetState
  /** Initial position on screen */
  initialPosition?: Point
  /** Speech bubble message to display above pet */
  message?: string | null
  /** Callback when user clicks the pet */
  onClick?: () => void
}

const NOMINAL_PET_PX = 96

function clampPoint(x: number, y: number, w: number, h: number): Point {
  const winW = typeof window !== 'undefined' ? window.innerWidth || 800 : 800
  const winH = typeof window !== 'undefined' ? window.innerHeight || 600 : 600
  return {
    x: Math.min(Math.max(0, x), Math.max(0, winW - w)),
    y: Math.min(Math.max(0, y), Math.max(0, winH - h))
  }
}

function facing(leftX: number, petW: number): string {
  const winW = typeof window !== 'undefined' ? window.innerWidth || 800 : 800
  return leftX + petW / 2 < winW / 2 ? 'scaleX(-1)' : 'none'
}

export function HermesSprite({
  src = '/sprites/hermes-sprites/hermes-sprite.png',
  info: infoProp,
  roam = true,
  scale: scaleProp,
  state: stateProp,
  initialPosition,
  message,
  onClick
}: HermesSpriteProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const spriteWrapRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{ dx: number; dy: number; x: number; y: number } | null>(null)

  const [position, setPosition] = useState<Point>(() => {
    if (initialPosition) return initialPosition
    const winH = typeof window !== 'undefined' ? window.innerHeight || 600 : 600
    return clampPoint(24, winH - 180, NOMINAL_PET_PX, NOMINAL_PET_PX)
  })

  const [curState, setCurState] = useState<PetState>(stateProp ?? 'idle')
  const [roamDir, setRoamDir] = useState<-1 | 0 | 1>(0)

  const effectiveState = stateProp ?? curState

  const petInfo: PetInfo = {
    src,
    frameW: DEFAULT_FRAME_W,
    frameH: DEFAULT_FRAME_H,
    scale: scaleProp ?? DEFAULT_SCALE,
    ...infoProp
  }

  const petW = (petInfo.frameW ?? DEFAULT_FRAME_W) * (petInfo.scale ?? DEFAULT_SCALE)
  const petH = (petInfo.frameH ?? DEFAULT_FRAME_H) * (petInfo.scale ?? DEFAULT_SCALE)

  const shadowW = Math.round(petW * 0.55)
  const shadowH = Math.max(3, Math.round(shadowW * 0.28))

  const clamp = useCallback((pt: Point): Point => clampPoint(pt.x, pt.y, petW, petH), [petW, petH])

  const onStateChange = useCallback((newState: PetState, newDir: -1 | 0 | 1) => {
    setCurState(newState)
    setRoamDir(newDir)
  }, [])

  const onPositionCommit = useCallback((pt: Point) => {
    setPosition(pt)
  }, [])

  const isDragging = useCallback(() => dragRef.current !== null, [])

  usePetRoam({
    enabled: roam && !isDragging(),
    containerRef,
    isInteracting: isDragging,
    petW,
    petH,
    loopMs: petInfo.loopMs ?? 1100,
    onStateChange,
    onPositionCommit
  })

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const el = containerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top, x: rect.left, y: rect.top }
      el.setPointerCapture(e.pointerId)
      el.style.cursor = 'grabbing'
    },
    []
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current
      const el = containerRef.current
      if (!drag || !el) return

      const next = clamp({ x: e.clientX - drag.dx, y: e.clientY - drag.dy })
      drag.x = next.x
      drag.y = next.y

      el.style.left = `${next.x}px`
      el.style.top = `${next.y}px`

      if (spriteWrapRef.current) {
        spriteWrapRef.current.style.transform = facing(next.x, petW)
      }
    },
    [clamp, petW]
  )

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current
      if (drag) {
        dragRef.current = null
        setPosition({ x: drag.x, y: drag.y })
      }
      const el = containerRef.current
      if (el) {
        el.style.cursor = 'grab'
        el.releasePointerCapture?.(e.pointerId)
      }
    },
    []
  )

  const walk = roamWalkRow(roamDir, petInfo.stateRows)

  return (
    <div
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      ref={containerRef}
      style={{
        cursor: 'grab',
        left: position.x,
        pointerEvents: 'auto',
        position: 'fixed',
        top: position.y,
        touchAction: 'none',
        userSelect: 'none',
        zIndex: 9999
      }}
    >
      {/* Speech Bubble */}
      {message && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%) translateY(-8px)',
            background: 'rgba(15, 23, 42, 0.9)',
            backdropFilter: 'blur(8px)',
            color: '#34d399',
            border: '1px solid rgba(52, 211, 153, 0.3)',
            padding: '6px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            pointerEvents: 'none',
            zIndex: 2
          }}
        >
          {message}
        </div>
      )}

      {/* Radial Ground Shadow */}
      <div
        aria-hidden
        style={{
          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 70%)',
          bottom: -shadowH * 0.4,
          height: shadowH,
          left: '50%',
          pointerEvents: 'none',
          position: 'absolute',
          transform: 'translateX(-50%)',
          width: shadowW,
          zIndex: 0
        }}
      />

      {/* Sprite Canvas Wrapper */}
      <div
        ref={spriteWrapRef}
        style={{
          lineHeight: 0,
          position: 'relative',
          transform: roamDir !== 0 ? (walk.mirror ? 'scaleX(-1)' : 'none') : facing(position.x, petW),
          zIndex: 1
        }}
      >
        <PetCanvasSprite info={petInfo} rowOverride={walk.row} state={effectiveState} />
      </div>
    </div>
  )
}
